<template>
  <v-card>
    <!-- Bulk Actions Toolbar -->
    <v-toolbar v-if="selectedItemsModel.length > 0" color="primary" dark density="compact">
      <v-toolbar-title>
        {{ selectedItemsModel.length }} request(s) selected
        <span v-if="filledRequestIds.length < selectedItemsModel.length && filledRequestIds.length > 0" class="text-caption ml-2">
          ({{ filledRequestIds.length }} filled)
        </span>
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn
        v-if="isAdmin"
        variant="text"
        @click="$emit('bulkEdit', selectedItemsModel)"
        prepend-icon="mdi-pencil"
      >
        Bulk Edit
      </v-btn>
      <v-btn
        v-if="anySelectedAreFilled"
        variant="text"
        @click="$emit('bulkMarkDelivered', filledRequestIds)"
        prepend-icon="mdi-truck-delivery"
      >
        Mark Delivered
      </v-btn>
      <v-btn
        v-if="anySelectedAreFilled"
        variant="text"
        @click="$emit('printDeliverySlips', filledRequestIds)"
        prepend-icon="mdi-printer"
      >
        Print Delivery Slips
      </v-btn>
    </v-toolbar>

    <v-data-table
      :headers="headers"
      :items="requests"
      :loading="loading"
      :items-per-page="10"
      :show-select="true"
      v-model="selectedItemsModel"
      item-value="id"
      @click:row="(event, { item }) => $emit('selectRequest', item)"
      class="clickable-table"
    >
      <!-- Student Column -->
      <template v-slot:item.student_name="{ item }">
        <div class="d-flex align-center ga-3">
          <v-avatar color="secondary" size="32">
            <span class="text-white">{{ getInitials((item as any).student_name) }}</span>
          </v-avatar>
          <span>{{ (item as any).student_name || 'Unknown' }}</span>
        </div>
      </template>

      <!-- School Column -->
      <template v-slot:item.school="{ item }">
        <span>{{ getSchoolName((item as any).school) }}</span>
      </template>

      <!-- Equipment Type Column -->
      <template v-slot:item.equipment_type="{ item }">
        <v-chip size="small">
          {{ formatEquipmentType((item as any).equipment_type) }}
        </v-chip>
      </template>

      <!-- Min Rating Column -->
      <template v-slot:item.minimum_rating="{ item }">
        <v-chip size="small" color="primary">
          {{ (item as any).minimum_rating || 'N/A' }}
        </v-chip>
      </template>

      <!-- Status Column -->
      <template v-slot:item.status="{ item }">
        <v-chip
          :color="getRequestStatusColor((item as any).status)"
          size="small"
          variant="tonal"
        >
          {{ formatStatus((item as any).status) }}
        </v-chip>
      </template>

      <!-- Date Column -->
      <template v-slot:item.submitted_at="{ item }">
        {{ formatDate((item as any).submitted_at) }}
      </template>

      <!-- Actions Column -->
      <template v-slot:item.actions="{ item }">
        <div class="d-flex ga-1" @click.stop>
          <!-- Fill button for pending requests -->
          <v-btn
            v-if="(item as any).status === 'pending'"
            color="info"
            size="small"
            @click="$emit('fill', item as any)"
          >
            <v-icon start size="small">mdi-package-variant</v-icon>
            Fill
          </v-btn>
          <!-- Delivered button for filled requests -->
          <v-btn
            v-if="(item as any).status === 'filled'"
            color="success"
            size="small"
            @click="$emit('markDelivered', item as any)"
          >
            <v-icon start size="small">mdi-truck-delivery</v-icon>
            Deliver
          </v-btn>
          <!-- Edit button for admins -->
          <v-btn
            v-if="isAdmin"
            icon="mdi-pencil"
            size="small"
            variant="text"
            @click="$emit('edit', item as any)"
            title="Edit Request"
          ></v-btn>
          <!-- Delete button for admins -->
          <v-btn
            v-if="isAdmin"
            icon="mdi-delete"
            size="small"
            variant="text"
            color="error"
            @click="$emit('delete', item as any)"
            title="Delete Request"
          ></v-btn>
        </div>
      </template>

      <!-- Loading -->
      <template v-slot:loading>
        <v-skeleton-loader type="table-row@10"></v-skeleton-loader>
      </template>

      <!-- No Data -->
      <template v-slot:no-data>
        <v-alert type="info" variant="tonal" class="ma-4">
          No requests found
        </v-alert>
      </template>
    </v-data-table>
  </v-card>
</template>

<script setup lang="ts">
/* eslint-disable no-undef */
import { computed } from 'vue'

interface Request {
  id: string
  student_name?: string
  school: string
  grade_level: string
  equipment_type: string
  minimum_rating?: number
  status: string
  submitted_at: string
}

interface School {
  id: string
  _id?: string
  name: string
}

interface Props {
  requests: Request[]
  loading?: boolean
  isAdmin?: boolean
  schools: School[]
  selectedItems?: string[]
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  isAdmin: false,
  requests: () => [],
  schools: () => [],
  selectedItems: () => []
})

const emit = defineEmits(['selectRequest', 'fill', 'markDelivered', 'edit', 'delete', 'update:selectedItems', 'bulkEdit', 'bulkMarkDelivered', 'printDeliverySlips'])

const selectedItemsModel = computed({
  get: () => props.selectedItems,
  set: (value) => emit('update:selectedItems', value)
})

// Check if any selected requests have 'filled' status
const anySelectedAreFilled = computed(() => {
  if (selectedItemsModel.value.length === 0) return false
  return selectedItemsModel.value.some(id => {
    const request = props.requests.find(r => r.id === id)
    return request && request.status === 'filled'
  })
})

// Get only the filled request IDs from selection
const filledRequestIds = computed(() => {
  return selectedItemsModel.value.filter(id => {
    const request = props.requests.find(r => r.id === id)
    return request && request.status === 'filled'
  })
})

const headers = [
  { title: 'Student', key: 'student_name', sortable: true },
  { title: 'School', key: 'school', sortable: true },
  { title: 'Grade', key: 'grade_level', sortable: true },
  { title: 'Asset Tag', key: 'assigned_asset_tag', sortable: true },
  { title: 'Status', key: 'status', sortable: true },
  { title: 'Submitted', key: 'submitted_at', sortable: true },
  { title: 'Actions', key: 'actions', sortable: false }
]

const getInitials = (name: string) => {
  if (!name) return '?'
  const parts = name.split(' ')
  if (parts.length >= 2) {
    return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase()
  }
  return name.charAt(0).toUpperCase()
}

const formatEquipmentType = (type: string) => {
  if (!type) return 'Unknown'
  return type.charAt(0).toUpperCase() + type.slice(1)
}

const formatStatus = (status: string) => {
  if (!status) return 'Unknown'
  return status.split('_').map(word =>
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ')
}

const formatDate = (date: string) => {
  if (!date) return 'N/A'
  return new Date(date).toLocaleDateString()
}

const getRequestStatusColor = (status: string) => {
  const statusColors: Record<string, string> = {
    pending: 'warning',
    filled: 'info',
    completed: 'success',
    denied: 'error'
  }
  return statusColors[status] || 'grey'
}

const getSchoolName = (schoolId: string) => {
  if (!schoolId) return 'N/A'
  const school = props.schools.find((s: School) => s.id === schoolId || s._id === schoolId)
  return school ? school.name : schoolId
}
</script>

<style scoped>
.clickable-table :deep(tbody tr) {
  cursor: pointer;
}

.clickable-table :deep(tbody tr:hover) {
  background-color: rgba(0, 0, 0, 0.04);
}
</style>
